import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import {
  subscribeUserThreads,
  subscribeAllParentThreads,
  subscribeManagerAlertThreads,
  subscribeThreadMessages,
  markThreadRead,
  sendThreadMessage,
  getOrCreateParentThread,
  getOrCreateThreadByAdmin,
  getParentsList
} from '../services/parentMessagingService';
import { FaPaperPlane, FaSearch, FaPlus, FaTimes, FaEnvelope, FaExclamationTriangle } from 'react-icons/fa';

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
};

const formatMessageTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return time;
  if (isYesterday) return `Yesterday ${time}`;
  return `${date.toLocaleDateString()} ${time}`;
};

const roleBadgeColor = (role) => {
  switch (role) {
    case 'administrator': return { bg: '#7c3aed', text: '#fff' };
    case 'manager': return { bg: '#2563eb', text: '#fff' };
    case 'parent': return { bg: '#059669', text: '#fff' };
    case 'system': return { bg: '#dc2626', text: '#fff' };
    default: return { bg: '#4b5563', text: '#fff' };
  }
};

const formatAnomalyTime = (iso) => {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleString();
  } catch (_err) {
    return iso;
  }
};

const AlertMessageContent = ({ message }) => {
  const data = message?.data;
  if (!data || !Array.isArray(data.anomalies) || data.anomalies.length === 0) {
    const fallbackText = message?.text || 'Anomaly digest delivered.';
    return (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{fallbackText}</div>
        {data?.windowLabel && (
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            Window: {data.windowLabel}
            {data.speedThreshold ? ` | Threshold: ${data.speedThreshold} km/h` : ''}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <FaExclamationTriangle style={{ color: '#dc2626' }} />
        {data.anomalyCount ?? data.anomalies.length} anomalies — {data.windowLabel || 'Recent activity'}
      </div>
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 10 }}>
        Speed threshold: {data.speedThreshold ?? 60} km/h{data.timeZone ? ` | Timezone: ${data.timeZone}` : ''}
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Driver</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Vehicle</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Anomaly</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>Speed</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>Road</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Coordinates</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>Location</th>
              <th style={{ padding: '6px 8px', borderBottom: '1px solid #e5e7eb' }}>When</th>
            </tr>
          </thead>
          <tbody>
            {data.anomalies.map((row, idx) => (
              <tr key={row.eventId || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.driverName || '—'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.vehicleReg || '—'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.anomaly || '—'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                  {Number.isFinite(row.vehicleSpeed) ? Math.round(row.vehicleSpeed) : '—'}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', textAlign: 'right' }}>
                  {row.roadSpeed > 0 ? Math.round(row.roadSpeed) : '—'}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                  {row.mapsUrl ? (
                    <a
                      href={row.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#2563eb', textDecoration: 'none' }}
                    >
                      {row.coordinates}
                    </a>
                  ) : (row.coordinates || 'Unknown')}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{row.location || 'Unknown'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>{formatAnomalyTime(row.lastUpdated)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MessagesPage = () => {
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [parentProfile, setParentProfile] = useState(null);
  const [parentThreads, setParentThreads] = useState([]);
  const [alertThreads, setAlertThreads] = useState([]);
  const [activeTab, setActiveTab] = useState('parents');
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [parentsList, setParentsList] = useState([]);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [loadingParents, setLoadingParents] = useState(false);
  const [startingConversation, setStartingConversation] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    setCurrentUserId(user.uid);

    const fetchUser = async () => {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserInfo(data);

        if (data.role === 'parent') {
          if (data.parentRef) {
            const parentDoc = await getDoc(doc(db, 'parents', data.parentRef));
            if (parentDoc.exists()) setParentProfile({ id: parentDoc.id, ...parentDoc.data() });
          } else if (data.parentId) {
            const parentByIdQuery = query(
              collection(db, 'parents'),
              where('parentId', '==', data.parentId),
              limit(1)
            );
            const parentSnapshot = await getDocs(parentByIdQuery);
            if (!parentSnapshot.empty) {
              setParentProfile({ id: parentSnapshot.docs[0].id, ...parentSnapshot.docs[0].data() });
            }
          }
        }
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    if (!currentUserId) return undefined;

    const isStaff = userInfo?.role === 'administrator' || userInfo?.role === 'manager';

    if (isStaff) {
      const unsubParents = subscribeAllParentThreads((threadList) => {
        setParentThreads(threadList);
      });
      const unsubAlerts = subscribeManagerAlertThreads(currentUserId, (threadList) => {
        setAlertThreads(threadList);
      });
      return () => {
        unsubParents && unsubParents();
        unsubAlerts && unsubAlerts();
      };
    }

    const unsub = subscribeUserThreads(currentUserId, (threadList) => {
      setParentThreads(threadList);
    });
    return () => unsub && unsub();
  }, [currentUserId, userInfo?.role]);

  const isStaff = userInfo?.role === 'administrator' || userInfo?.role === 'manager';

  const visibleThreads = useMemo(() => {
    if (isStaff && activeTab === 'alerts') return alertThreads;
    return parentThreads;
  }, [isStaff, activeTab, alertThreads, parentThreads]);

  useEffect(() => {
    if (!selectedThreadId && visibleThreads.length > 0) {
      setSelectedThreadId(visibleThreads[0].id);
    }
    if (selectedThreadId && !visibleThreads.some((t) => t.id === selectedThreadId)) {
      setSelectedThreadId(visibleThreads[0]?.id || null);
    }
  }, [visibleThreads, selectedThreadId]);

  const totalAlertUnread = useMemo(() => {
    if (!isStaff || !currentUserId) return 0;
    return alertThreads.reduce((acc, t) => acc + (t.unreadBy?.[currentUserId] || 0), 0);
  }, [alertThreads, currentUserId, isStaff]);

  const totalParentUnread = useMemo(() => {
    if (!currentUserId) return 0;
    return parentThreads.reduce((acc, t) => acc + (t.unreadBy?.[currentUserId] || 0), 0);
  }, [parentThreads, currentUserId]);

  useEffect(() => {
    if (!selectedThreadId || !currentUserId) {
      setMessages([]);
      return;
    }
    const unsub = subscribeThreadMessages(selectedThreadId, (msgs) => {
      setMessages(msgs);
    });
    markThreadRead({ threadId: selectedThreadId, userId: currentUserId }).catch(() => {});
    return () => unsub();
  }, [selectedThreadId, currentUserId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (messages.length > 0) scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!selectedThreadId || !currentUserId || !draft.trim()) return;
    try {
      await sendThreadMessage({
        threadId: selectedThreadId,
        text: draft,
        sender: {
          id: currentUserId,
          name: `${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`.trim() || userInfo?.email || 'User',
          role: userInfo?.role || 'user'
        }
      });
      setDraft('');
    } catch {
      setStatus('Failed to send message.');
    }
  };

  const handleStartParentConversation = async () => {
    if (!parentProfile) {
      setStatus('Parent profile is not linked yet.');
      return;
    }
    try {
      setStartingConversation(true);
      setStatus('');
      const thread = await getOrCreateParentThread({ parentProfile });
      setSelectedThreadId(thread.id);
      setStatus('');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      setStatus('Could not start a conversation. Please try again.');
    } finally {
      setStartingConversation(false);
    }
  };

  const handleNewConversationOpen = async () => {
    setShowNewConversation(true);
    setParentSearchQuery('');
    if (parentsList.length === 0) {
      setLoadingParents(true);
      try {
        const list = await getParentsList();
        setParentsList(list);
      } catch {
        setStatus('Could not load parents list.');
      } finally {
        setLoadingParents(false);
      }
    }
  };

  const handleSelectParent = async (parentDoc) => {
    try {
      setStatus('');
      setShowNewConversation(false);
      const thread = await getOrCreateThreadByAdmin({
        parentDoc,
        adminUser: auth.currentUser
      });
      setSelectedThreadId(thread.id);
    } catch {
      setStatus('Could not start conversation with this parent.');
    }
  };

  const filteredThreads = visibleThreads.filter((t) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (t.parentName || '').toLowerCase().includes(q) ||
      (t.managerName || '').toLowerCase().includes(q) ||
      (t.lastMessage || '').toLowerCase().includes(q) ||
      (t.subject || '').toLowerCase().includes(q)
    );
  });

  const filteredParents = parentsList.filter((p) => {
    if (!parentSearchQuery.trim()) return true;
    const q = parentSearchQuery.toLowerCase();
    return (
      `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.phone || '').toLowerCase().includes(q)
    );
  });

  const selectedThread = visibleThreads.find((t) => t.id === selectedThreadId);
  const isAlertThread = selectedThread?.kind === 'managerAlert';

  return (
    <div style={{ padding: '24px', height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1a1f2e', margin: 0 }}>Messages</h1>
        <p style={{ color: '#6b7280', margin: '4px 0 0' }}>
          {isStaff ? 'Manage conversations with parents' : 'Chat with school transport team'}
        </p>
      </div>

      <div style={{
        flex: 1, display: 'flex', background: '#fff', borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden', minHeight: 0
      }}>
        {/* Thread sidebar */}
        <div style={{
          width: 340, borderRight: '1px solid #e5e7eb', display: 'flex',
          flexDirection: 'column', flexShrink: 0
        }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
            {isStaff && (
              <div style={{
                display: 'flex', gap: 4, marginBottom: 12,
                background: '#f3f4f6', borderRadius: 8, padding: 4
              }}>
                {[
                  { id: 'parents', label: 'Parents', unread: totalParentUnread },
                  { id: 'alerts', label: 'Alerts', unread: totalAlertUnread }
                ].map((tab) => {
                  const selected = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setSelectedThreadId(null);
                        setShowNewConversation(false);
                      }}
                      style={{
                        flex: 1, border: 'none', cursor: 'pointer',
                        background: selected ? '#fff' : 'transparent',
                        boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                        color: selected ? '#1a1f2e' : '#6b7280',
                        fontWeight: selected ? 700 : 500,
                        padding: '8px 10px', borderRadius: 6, fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                      }}
                    >
                      <span>{tab.label}</span>
                      {tab.unread > 0 && (
                        <span style={{
                          background: tab.id === 'alerts' ? '#dc2626' : '#00b8b8',
                          color: '#fff', borderRadius: 10, padding: '1px 7px',
                          fontSize: '0.7rem', fontWeight: 700
                        }}>
                          {tab.unread}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <div style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 8,
                background: '#f3f4f6', borderRadius: 8, padding: '8px 12px'
              }}>
                <FaSearch style={{ color: '#9ca3af', fontSize: '0.85rem' }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={activeTab === 'alerts' ? 'Search alerts...' : 'Search conversations...'}
                  style={{
                    border: 'none', background: 'transparent', outline: 'none',
                    fontSize: '0.9rem', color: '#1a1f2e', width: '100%'
                  }}
                />
              </div>
              {isStaff && activeTab === 'parents' && (
                <button
                  onClick={handleNewConversationOpen}
                  title="New conversation"
                  style={{
                    background: '#00b8b8', border: 'none', color: '#fff',
                    borderRadius: 8, width: 38, height: 38, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, fontSize: '1rem'
                  }}
                >
                  <FaPlus />
                </button>
              )}
            </div>
            {userInfo?.role === 'parent' && parentThreads.length === 0 && (
              <div>
                <button
                  onClick={handleStartParentConversation}
                  disabled={startingConversation}
                  style={{
                    background: startingConversation ? '#6b7280' : '#00b8b8',
                    border: 'none', color: '#fff',
                    borderRadius: 8, padding: '10px 16px',
                    cursor: startingConversation ? 'wait' : 'pointer',
                    width: '100%', fontWeight: 600, fontSize: '0.9rem',
                    opacity: startingConversation ? 0.7 : 1
                  }}
                >
                  {startingConversation ? 'Starting...' : 'Start a conversation'}
                </button>
                {status && (
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: '#dc2626', textAlign: 'center' }}>
                    {status}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filteredThreads.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
                {searchQuery ? 'No matching conversations.' : (
                  isStaff
                    ? (activeTab === 'alerts'
                        ? 'No anomaly digests yet. Enable Driver Anomaly Email Digest in Settings.'
                        : 'No conversations yet. Parents will appear here when they reach out, or start one using the + button.')
                    : 'No conversations yet.'
                )}
              </div>
            ) : (
              filteredThreads.map((thread) => {
                const unread = thread.unreadBy?.[currentUserId] || 0;
                const isSelected = thread.id === selectedThreadId;
                const isAlert = thread.kind === 'managerAlert';
                const title = isAlert
                  ? (thread.subject || 'Anomaly Digest')
                  : (thread.parentName || thread.subject || 'Conversation');
                return (
                  <div
                    key={thread.id}
                    onClick={() => setSelectedThreadId(thread.id)}
                    style={{
                      padding: '14px 16px', cursor: 'pointer',
                      borderBottom: '1px solid #f3f4f6',
                      background: isSelected ? (isAlert ? '#fef2f2' : '#f0fdfa') : '#fff',
                      borderLeft: isSelected
                        ? (isAlert ? '3px solid #dc2626' : '3px solid #00b8b8')
                        : '3px solid transparent',
                      transition: 'background 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontWeight: unread > 0 ? 700 : 600, fontSize: '0.95rem',
                        color: '#1a1f2e', display: 'flex', alignItems: 'center', gap: 6,
                        minWidth: 0, flex: 1
                      }}>
                        {isAlert && (
                          <FaExclamationTriangle
                            style={{ color: '#dc2626', fontSize: '0.75rem', flexShrink: 0 }}
                          />
                        )}
                        <span style={{
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {title}
                        </span>
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', flexShrink: 0, marginLeft: 8 }}>
                        {formatRelativeTime(thread.lastMessageAt)}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '0.85rem', color: unread > 0 ? '#374151' : '#9ca3af',
                      fontWeight: unread > 0 ? 600 : 400,
                      marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden',
                      textOverflow: 'ellipsis', maxWidth: '100%'
                    }}>
                      {thread.lastMessage || 'No messages yet'}
                    </div>
                    {unread > 0 && (
                      <span style={{
                        display: 'inline-block',
                        background: isAlert ? '#dc2626' : '#00b8b8', color: '#fff',
                        borderRadius: 10, padding: '2px 8px', fontSize: '0.7rem',
                        fontWeight: 700, marginTop: 4
                      }}>
                        {unread} new
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Conversation panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {showNewConversation ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{
                padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <span style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1f2e' }}>
                  New Conversation — Select a Parent
                </span>
                <button
                  onClick={() => setShowNewConversation(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '1.1rem' }}
                >
                  <FaTimes />
                </button>
              </div>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: '#f3f4f6', borderRadius: 8, padding: '8px 12px'
                }}>
                  <FaSearch style={{ color: '#9ca3af', fontSize: '0.85rem' }} />
                  <input
                    type="text"
                    value={parentSearchQuery}
                    onChange={(e) => setParentSearchQuery(e.target.value)}
                    placeholder="Search parents by name, email, or phone..."
                    style={{
                      border: 'none', background: 'transparent', outline: 'none',
                      fontSize: '0.9rem', color: '#1a1f2e', width: '100%'
                    }}
                  />
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                {loadingParents ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>Loading parents...</div>
                ) : filteredParents.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>
                    {parentSearchQuery ? 'No matching parents found.' : 'No parents available.'}
                  </div>
                ) : (
                  filteredParents.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => handleSelectParent(p)}
                      style={{
                        padding: '12px 20px', cursor: 'pointer',
                        borderBottom: '1px solid #f3f4f6',
                        display: 'flex', alignItems: 'center', gap: 12,
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdfa'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%', background: '#059669',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: '0.9rem', flexShrink: 0
                      }}>
                        {(p.firstName?.[0] || '').toUpperCase()}{(p.lastName?.[0] || '').toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: '#1a1f2e', fontSize: '0.95rem' }}>
                          {p.firstName} {p.lastName}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {p.email || p.phone || 'No contact info'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : selectedThreadId ? (
            <>
              <div style={{
                padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a1f2e', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isAlertThread && <FaExclamationTriangle style={{ color: '#dc2626' }} />}
                    {isAlertThread
                      ? (selectedThread?.subject || 'Anomaly Digest')
                      : (selectedThread?.parentName || 'Conversation')}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {isAlertThread
                      ? `Anomaly Digest${selectedThread?.windowLabel ? ` - ${selectedThread.windowLabel}` : ''}`
                      : (selectedThread?.subject || 'Parent Support')}
                    {selectedThread?.status === 'closed' && (
                      <span style={{
                        marginLeft: 8, background: '#fef2f2', color: '#dc2626',
                        padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600
                      }}>
                        Closed
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#fafbfc' }}>
                {messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: 60 }}>
                    {isAlertThread ? 'No anomaly entries yet.' : 'No messages yet. Start the conversation below.'}
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isOwn = msg.senderId === currentUserId;
                    const isSystemMessage = msg.senderRole === 'system' || msg.senderId === 'system';
                    const hasAnomalyTable = isSystemMessage && msg.data && Array.isArray(msg.data.anomalies);
                    const badge = roleBadgeColor(msg.senderRole);
                    const alignSelf = isSystemMessage ? 'stretch' : (isOwn ? 'flex-end' : 'flex-start');
                    return (
                      <div
                        key={msg.id}
                        style={{
                          marginBottom: 16, display: 'flex', flexDirection: 'column',
                          alignItems: isSystemMessage ? 'stretch' : (isOwn ? 'flex-end' : 'flex-start')
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: '0.8rem', color: '#6b7280', fontWeight: 500 }}>
                            {msg.senderName}
                          </span>
                          <span style={{
                            fontSize: '0.65rem', padding: '1px 7px', borderRadius: 4,
                            background: badge.bg, color: badge.text, fontWeight: 600,
                            textTransform: 'capitalize'
                          }}>
                            {msg.senderRole || 'user'}
                          </span>
                        </div>
                        <div style={{
                          background: isSystemMessage ? '#fff' : (isOwn ? '#00b8b8' : '#fff'),
                          color: isSystemMessage ? '#1a1f2e' : (isOwn ? '#fff' : '#1a1f2e'),
                          borderRadius: isSystemMessage ? 12 : (isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px'),
                          padding: hasAnomalyTable ? '14px 16px' : '10px 16px',
                          maxWidth: isSystemMessage ? '100%' : '70%',
                          alignSelf,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          wordBreak: 'break-word', fontSize: '0.95rem', lineHeight: 1.5,
                          border: isSystemMessage ? '1px solid #fde2e2' : 'none'
                        }}>
                          {hasAnomalyTable ? (
                            <AlertMessageContent message={msg} />
                          ) : (
                            <span>{msg.text}</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 3 }}>
                          {formatMessageTime(msg.createdAt)}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {isAlertThread ? (
                <div style={{
                  borderTop: '1px solid #e5e7eb', padding: '14px 20px',
                  background: '#fff', textAlign: 'center', color: '#6b7280',
                  fontSize: '0.85rem'
                }}>
                  Anomaly digests are read-only. Adjust frequency, threshold, or CC managers in <strong>Settings</strong>.
                </div>
              ) : (
                <div style={{
                  borderTop: '1px solid #e5e7eb', padding: '12px 20px',
                  display: 'flex', gap: 10, alignItems: 'center', background: '#fff'
                }}>
                  <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Type a message..."
                    style={{
                      flex: 1, border: '1px solid #e5e7eb', borderRadius: 10,
                      padding: '10px 14px', fontSize: '0.95rem', outline: 'none',
                      color: '#1a1f2e', background: '#f9fafb'
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#00b8b8'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleSend(); }
                    }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!draft.trim()}
                    style={{
                      background: draft.trim() ? '#00b8b8' : '#d1d5db',
                      border: 'none', color: '#fff', borderRadius: 10,
                      width: 42, height: 42, cursor: draft.trim() ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem', transition: 'background 0.15s'
                    }}
                  >
                    <FaPaperPlane />
                  </button>
                </div>
              )}
              {status && (
                <div style={{ padding: '6px 20px', color: '#dc2626', fontSize: '0.8rem', background: '#fff' }}>
                  {status}
                </div>
              )}
            </>
          ) : (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', color: '#9ca3af'
            }}>
              <FaEnvelope style={{ fontSize: '3rem', marginBottom: 16, color: '#d1d5db' }} />
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#6b7280' }}>
                Select a conversation
              </div>
              <div style={{ fontSize: '0.9rem', marginTop: 4 }}>
                Choose a thread from the sidebar to start messaging.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
